import React from 'react';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { InlineMessage, LoadingState } from '../components/ui';
import MarketingSurface from '../components/MarketingSurface';

interface InvitationDetail {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  organization: { id: string; name: string; type: string } | null;
}

/**
 * Public route: `/invitations/accept?token=...`. This is the link the
 * invitation email actually sends, so it has to work for a stranger who has
 * never signed in — no `AuthGuard`, no shell chrome.
 */
export const InvitationAcceptPage: React.FC = () => {
  const { t } = useAppTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const { showToast } = useToast();

  const returnPath = `/invitations/accept?token=${encodeURIComponent(token)}`;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['invitation-token', token],
    queryFn: async () => {
      // Public endpoint — no auth headers, this must work before sign-in.
      const res = await makeApiCall(`/invitations/token/${encodeURIComponent(token)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('invitations.loadError'));
      }
      return body.data as InvitationDetail;
    },
    enabled: Boolean(token),
    retry: false,
    staleTime: 30_000,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/invitations/accept', {
        method: 'POST',
        headers,
        body: JSON.stringify({ token }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('invitations.acceptError'));
      }
      return body;
    },
    onSuccess: () => {
      showToast(t('invitations.accepted'), 'success');
      navigate('/app/dashboard');
    },
    onError: (err) => {
      showToast(err instanceof Error ? err.message : t('invitations.acceptError'), 'error');
    },
  });

  let body: React.ReactNode;

  if (!token) {
    body = <InlineMessage severity="error">{t('invitations.missingToken')}</InlineMessage>;
  } else if (isLoading) {
    body = <LoadingState message={t('invitations.loading')} size="page" />;
  } else if (isError || !data) {
    body = (
      <>
        <InlineMessage severity="error">
          {error instanceof Error ? error.message : t('invitations.loadError')}
        </InlineMessage>
        <Button component={RouterLink} to="/contact" variant="outlined" fullWidth sx={{ mt: 1 }}>
          {t('invitations.requestNewInvite')}
        </Button>
      </>
    );
  } else if (data.status === 'accepted') {
    body = (
      <>
        <InlineMessage severity="info">{t('invitations.alreadyAccepted')}</InlineMessage>
        <Button component={RouterLink} to="/login" variant="contained" fullWidth sx={{ mt: 1 }}>
          {t('auth.signIn')}
        </Button>
      </>
    );
  } else if (data.status === 'cancelled') {
    body = (
      <>
        <InlineMessage severity="warning">{t('invitations.cancelled')}</InlineMessage>
        <Button component={RouterLink} to="/contact" variant="outlined" fullWidth sx={{ mt: 1 }}>
          {t('invitations.requestNewInvite')}
        </Button>
      </>
    );
  } else if (data.status === 'expired') {
    body = (
      <>
        <InlineMessage severity="warning">{t('invitations.expired')}</InlineMessage>
        <Button component={RouterLink} to="/contact" variant="outlined" fullWidth sx={{ mt: 1 }}>
          {t('invitations.requestNewInvite')}
        </Button>
      </>
    );
  } else if (authLoading) {
    body = <LoadingState message={t('invitations.loading')} size="page" />;
  } else if (!currentUser) {
    body = (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('invitations.previewBody', {
            organization: data.organization?.name || t('invitations.anOrganization'),
            role: data.role,
          })}
        </Typography>
        <InlineMessage severity="info">{t('invitations.signInPrompt')}</InlineMessage>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button
            component={RouterLink}
            to={`/login?redirect=${encodeURIComponent(returnPath)}`}
            variant="contained"
            fullWidth
          >
            {t('auth.signIn')}
          </Button>
          <Button
            component={RouterLink}
            to={`/register?redirect=${encodeURIComponent(returnPath)}`}
            variant="outlined"
            fullWidth
          >
            {t('auth.createAccount')}
          </Button>
        </Stack>
      </>
    );
  } else if (currentUser.email?.toLowerCase() !== data.email.toLowerCase()) {
    body = (
      <>
        <InlineMessage severity="error">
          {t('invitations.emailMismatch', { invited: data.email, current: currentUser.email || '' })}
        </InlineMessage>
        <Button
          component={RouterLink}
          to={`/login?redirect=${encodeURIComponent(returnPath)}`}
          variant="outlined"
          fullWidth
          sx={{ mt: 1 }}
        >
          {t('invitations.useDifferentAccount')}
        </Button>
      </>
    );
  } else {
    body = (
      <>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('invitations.previewBody', {
            organization: data.organization?.name || t('invitations.anOrganization'),
            role: data.role,
          })}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          disabled={acceptMutation.isPending}
          onClick={() => acceptMutation.mutate()}
        >
          {acceptMutation.isPending ? t('invitations.accepting') : t('invitations.accept')}
        </Button>
      </>
    );
  }

  return (
    <MarketingSurface>
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper sx={{ padding: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" gutterBottom textAlign="center">
              {t('invitations.title')}
            </Typography>
            <Box sx={{ mt: 2 }}>{body}</Box>
          </Paper>
        </Box>
      </Container>
    </MarketingSurface>
  );
};

export default InvitationAcceptPage;
